// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Injectable } from '@angular/core';
import { ApiService } from '../../api.service';
import { Observable, map, forkJoin, catchError, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  RefsNameMapping,
  // Molecule Tab Data
  StatsMoleculeData,
  MoleculeListData,
  MoleculeSizeDistribution,
  DistributionValue,
  DistributionTick,
  IntervalCountPerMapDistribution,
  // Molecule Json Data
  Stats0ResultsV1,
  Stats0ResultsV0,
  // Assembly Tab Data
  StatsAssemblyData,
  GenomeListData,
  AssembleChromosome,
  AssembleChromosomeItem,
  StatsTableAnchorCell,
  // Assembly Json Data
  AlignedAssemblyStatsV4,
  ChromosomeItem,
  // SV Tab Data
  StatsSVData,
  Svchart,
  SvChromosome,
  SvChromosomeItem,
  SvChrOpts,
  SvPieChartData,
  SvPieChartDataItem,
  // SV Json Data
  SvEnhancedHsv,
  SvEnhancedHsvSvsRecord,
  SvEnhancedHsvSvsRegion,
  SvJsonParse,
  SvfRec,
  SvfRecItem,
  SvfRecCheck,
  SvfRecCheck2,
  SvChrArray,
} from '../../../interfaces/hce/hce-stats';
import {
  StatsSvChromosomeColumns,
  StatsSvparam,
  StatsMessage,
} from '../../../shared/constants/hce/hce-stats-constants';
import { HceService } from '../hce.service';
import { MessageModalService } from '../../message-modal/message-modal.service';
import { SvResponse } from '../../../interfaces/hce/hce-sv';

@Injectable({
  providedIn: 'root'
})
export class HceStatsService {
  apiUrl: string = `${environment.apiBaseUrl}/api/v1`
  constructor( 
    private apiService: ApiService,
    private hceService: HceService,
    private messageModal: MessageModalService
  ) { }

  getStatsMolecule(analysisId: number): Observable<StatsMoleculeData> {
    return forkJoin({
      qcStatsRes: this.hceService.getQcStats(analysisId),
      gnderRes:  this.hceService.getGender(analysisId),
    }).pipe(
      map(({ qcStatsRes, gnderRes }) => {
        let {result, errorMessage} = this.makeStatsMoleculeData(qcStatsRes as Stats0ResultsV1 | Stats0ResultsV0);
        let smartydata: StatsMoleculeData = {
          errorMessage: errorMessage,
        };
        if (result) {
          smartydata.gender = gnderRes;
          smartydata.moleculeListData = result.moleculeListData;
          smartydata.moleculeSizeDistribution = result.moleculeSizeDistribution;
          smartydata.intervalCountDistribution = result.intervalCountDistribution;
          smartydata.success = true;
        }
        return smartydata;
      }),
      catchError((err) => {
        return throwError(() => err);
      })
    );
  }

  getStatsCoverage(
    analysisId: number,
    refNameMap: RefsNameMapping
  ): Observable<StatsAssemblyData> {

    return this.hceService.getAlignedAssemblyStats(analysisId).pipe(
      map((coverageRes) => {
        if (!coverageRes) {
          return {
            errorMessage: ['Empty assembly stats']
          };
        }

        const { result, errorMessage } =
          this.makeStatsCoveragetData(coverageRes, refNameMap);

        if (!result) {
          return { errorMessage };
        }

        return {
          assembleGenome: result.assembleGenome,
          assembleChromosome: result.assembleChromosome,
          success: true,
        };
      })
    );
  }

  getStatsSv(analysisId: number, panelId: number): Observable<StatsSVData> {
    return forkJoin({
      svJsonRes: this.hceService.getSvJson(analysisId),
      refsNameMappingRes: this.hceService.getRefsNameMapping(panelId),
    }).pipe(
      map(({ svJsonRes, refsNameMappingRes: mappedRefNameTable }) => {
        let smartydata: StatsSVData = {};
        if (!mappedRefNameTable) {
          smartydata = {
            errorMessage: [StatsMessage.ERROR_REFSNAMETABLE],
          };
        } else {
          smartydata = this.getStatsSampleSVData(svJsonRes as SvEnhancedHsv, mappedRefNameTable as RefsNameMapping);
        }

        return smartydata;
      }),
      catchError((err) => {
        return throwError(() => err);
      })
    );
  }

  private makeStatsMoleculeData(jsonData: Stats0ResultsV1 | Stats0ResultsV0 ) {
    if (!jsonData) {
      return {
        result: null,
        errorMessage: ['makeStatsMoleculeData failed. empty jsonData']
      };
    }

    if (jsonData.version) {
      const versionString = String(jsonData.version);
      const version = parseInt(versionString.replace(/[^0-9]/g, ''), 10);
      if (version >= 1) {
        return this.mapsetStatsParseV1(jsonData as Stats0ResultsV1);
      } else {
        return {
          result: null,
          errorMessage: ['mapsetStatsParseV1 error. (ver ' + version + ")"]
        };
      }
    } else {
        return this.mapsetStatsParseV0(jsonData as Stats0ResultsV0);
    }
  }

	private mapsetStatsParseV1(jsonData: Stats0ResultsV1){
    // items (key:label, val:value)
    let itemTable: MoleculeListData = [];
    itemTable.push({"key": "Total maps", "value": this.valtoComma(jsonData?.total_maps)});
    itemTable.push({"key": "Total length (Gb)", "value": this.valtoComma1000000(jsonData?.total_length)});
    itemTable.push({"key": "Mean length (Kb)", "value": this.valtoComma(jsonData?.length?.mean)});
    itemTable.push({"key": "Median length (Kb)", "value": this.valtoComma(jsonData?.length?.median)});
    itemTable.push({"key": "Min length (Kb)", "value": this.valtoComma(jsonData?.length?.min)});
    itemTable.push({"key": "Max length (Kb)", "value": this.valtoComma(jsonData?.length?.max)});
    itemTable.push({"key": "N50 length (Kb)", "value": this.valtoComma(jsonData?.N50_length)});
    itemTable.push({"key": "Estimated coverage", "value": this.valtoComma(jsonData?.estimated_coverage)});
    itemTable.push({"key": "Total intervals", "value": this.valtoComma(jsonData?.total_intervals)});
    itemTable.push({"key": "Mean interval count per map", "value": this.valtoComma(jsonData?.interval_count_per_map?.mean)});
    itemTable.push({"key": "Median interval count per map", "value": this.valtoComma(jsonData?.interval_count_per_map?.median)});
    itemTable.push({"key": "Min interval count per map", "value": this.valtoComma(jsonData?.interval_count_per_map?.min)});
    itemTable.push({"key": "Max interval count per map", "value": this.valtoComma(jsonData?.interval_count_per_map?.max)});
    itemTable.push({"key": "Maps with interval count at least 9", "value": this.valtoComma(jsonData?.maps_with_interval_count_at_least_9)});
    itemTable.push({"key": "Mean interval size", "value": this.valtoComma(jsonData?.interval_size?.mean)});
    itemTable.push({"key": "Median interval size", "value": this.valtoComma(jsonData?.interval_size?.median)});
    itemTable.push({"key": "Min interval size", "value": this.valtoComma(jsonData?.interval_size?.min)});
    itemTable.push({"key": "Max interval size", "value": this.valtoComma(jsonData?.interval_size?.max)});
          
    // interval_count_per_map_distribution
    let intervalCountPerMapDistribution: IntervalCountPerMapDistribution = {};
    if (jsonData?.interval_count_per_map_distribution) {
      let workV: DistributionValue = [];
      let workXtick: DistributionTick[] = [];
      let workYtick: DistributionTick = [];

      let ymax = 0;
      for (const [k, v] of jsonData.interval_count_per_map_distribution) {
        const key = String(k);
        const val = String(v);
        workV.push([key, val]);
        if (v > ymax) {
          ymax = v;
        }
      }

      // X tick
      workXtick = [
        ["0","100","200","300","400","500","600","700","800","900","1000"],
        ["0","50","100","150","200","250","300","350","400","450","500","550","600","650","700","750","800"],
        ["0","50","100","150","200","250","300","350","400","450","500","550","600"],
        ["0","50","100","150","200","250","300","350","400"],
        ["0","25","50","75","100","125","150","175","200"]
      ];

      // Y tick
      let digit = Math.pow(10, String(ymax).length -2 );
      let tmp = Math.ceil(ymax / digit);
      // Y軸目盛りを6区分
      while (tmp % 6 != 0) {
        tmp++;
      }
      ymax = (tmp * digit);

      for (let j = 0; j < 7; j++) {
        let t = (ymax / 6) * j;
        workYtick.push(String(t));
      }

      intervalCountPerMapDistribution = {
        v: workV,
        xtick: workXtick,
        ytick: workYtick,
      };
    }

    // map_length_distribu
    // tion
    let mapLengthDistribution: MoleculeSizeDistribution = {};
    if (jsonData?.map_length_distribution) {
      let workCv: DistributionValue = [];
      let workPv: DistributionValue = [];
      let workXtick : DistributionTick[] = [];
      let workYtick : DistributionTick = [];

      let total = 0 ;
      let cmax = 0;
      for (const [k, v] of jsonData.map_length_distribution) {
        const key = String(k);
        const val = Number(v);
        workCv.push([key, val]); //key, count
        total += val;
        if (val > cmax) {
          cmax = val;
        }
      }

      for (const [k, v] of jsonData.map_length_distribution) {
        const key = String(k);
        const val = Number(v);
        const p = (val != 0) ? (Math.round((val * 100 / total) * 10) / 10) : 0; // Round to one decimal place
        workPv.push([key, p]); //key, percentage
      }

      //X tick
      workXtick = [
        ["0","100","200","300","400","500","600","700","800","900","1000"],
        ["0","50","100","150","200","250","300","350","400","450","500","550","600","650","700","750","800"],
        ["0","50","100","150","200","250","300","350","400","450","500","550","600"],
        ["0","50","100","150","200","250","300","350","400"],
        ["0","25","50","75","100","125","150","175","200"]
      ];

      //Y tick
      let digit = Math.pow(10, String(cmax).length - 2);
      let tmp = Math.ceil(cmax / digit);

      // Y : 6 period
      while (tmp %6 != 0) {
        tmp++;
      }
      cmax = (tmp * digit);

      //ytick = array();
      for (let j = 0; j < 7; j++) {
        let t = (cmax / 6) * j;
        workYtick.push(String(t));
      }

      mapLengthDistribution = {
        c_v: workCv,
        p_v: workPv,
        xtick: workXtick,
        ytick: workYtick,
      };
    }

    return {
      result: {
        moleculeListData: itemTable,
        moleculeSizeDistribution: mapLengthDistribution,
        intervalCountDistribution: intervalCountPerMapDistribution,
      },
      errorMessage: []
    };
	}

	/**
	 * Mapset Stats parser ver 0
   * (statsdata.inc mapset_stats_parse_v0)
	 */
	private mapsetStatsParseV0(jsonData: Stats0ResultsV0){
    // items
    let moleculeTable: { key: string; value: any }[] = [];
    if (jsonData?.items) {
      let i = jsonData.items;
      if ('total_molecules' in (i ?? {} )) {
        moleculeTable.push({"key": "Total maps", "value": this.valtoComma(i?.total_molecules)});
      }

      moleculeTable.push({"key": "Total length (Gb)", "value": this.valtoComma(i?.total_length)});
      moleculeTable.push({"key": "Mean length (Kb)", "value": this.valtoComma(i?.mean_length)});
      moleculeTable.push({"key": "Median length (Kb)", "value": this.valtoComma(i?.median_length)});
      moleculeTable.push({"key": "Min length (Kb)", "value": this.valtoComma(i?.min_length)});
      moleculeTable.push({"key": "Max length (Kb)", "value": this.valtoComma(i?.max_length)});
      moleculeTable.push({"key": "Estimated coverage", "value": this.valtoComma(i?.estimated_coverage)});
      moleculeTable.push({"key": "Total intervals", "value": this.valtoComma(i?.total_fragments)});
      moleculeTable.push({"key": "Mean interval per mol", "value": this.valtoComma(i?.mean_frags_per_mol)});
      moleculeTable.push({"key": "Median interval per mol", "value": this.valtoComma(i?.median_frags_per_mol)});
      moleculeTable.push({"key": "Min interval count", "value": this.valtoComma(i?.min_frags_count)});
      moleculeTable.push({"key": "Max interval count", "value": this.valtoComma(i?.max_frags_count)});
      moleculeTable.push({"key": "Maps with size > 9", "value": this.valtoComma(i?.maps_with_size_me_than_9)});
      moleculeTable.push({"key": "Mean interval size", "value": this.valtoComma(i?.mean_fragment_size)});
      moleculeTable.push({"key": "Median interval size", "value": this.valtoComma(i?.median_fragment_size)});
      moleculeTable.push({"key": "Min interval size", "value": this.valtoComma(i?.min_fragment_size)});
      moleculeTable.push({"key": "Max interval size", "value": this.valtoComma(i?.max_fragment_size)});
    }

    // fragments_per_molecule
    let ymax = 0;
    let fragmentsPerMolecule = {};  // bar graph data
    if (jsonData?.fragments_per_molecule) {
      let workV: any[][] = [];
      let workXtick: any[][] = [];
      let workYtick: any[] = [];

      let ymax = 0;
      for (const [k, v] of jsonData.fragments_per_molecule) {
        const key = String(k);
        const val = Number(v);
        workV.push([key, String(val)]); //key, count
        if (val > ymax) {
          ymax = val;
        }
      }

      // X tick
      workXtick = [
        ["0","100","200","300","400","500","600","700","800","900","1000"],
        ["0","50","100","150","200","250","300","350","400","450","500","550","600","650","700","750","800"],
        ["0","50","100","150","200","250","300","350","400","450","500","550","600"],
        ["0","50","100","150","200","250","300","350","400"],
        ["0","25","50","75","100","125","150","175","200"]
      ];

      // Y tick
      let digit = Math.pow(10, String(ymax).length - 2);
      let tmp = Math.ceil(ymax / digit);
      // Divide the Y-axis into 6 intervals
      while (tmp % 6 != 0) {
        tmp++;
      }

      ymax = (tmp * digit);
      for (let j = 0; j < 7; j++) {
        let t = (ymax / 6) * j;
        workYtick.push(String(t));
      }

      fragmentsPerMolecule = {
        v: workV,
        xtick: workXtick,
        ytick: workYtick,
      };
    }

    // size distribution
    let sizeDistribution = {};
    if (jsonData?.size_distribution) {
      let workCv: any[][] = [];
      let workPv: any[][] = [];
      let workXtick : any[][] = [];
      let workYtick : any[] = [];

      let total = 0 ;
      let cmax = 0;
      for (const [k, v] of jsonData.size_distribution) {
        const key = String(k);
        const val = Number(v);
        workCv.push([key, val]); //key, count
        total += val;
        if (val > cmax) {
          cmax = val;
        }
      }

      for (const [k, v] of jsonData.size_distribution) {
        const key = String(k);
        const val = Number(v);
        const p = (val != 0) ? (Math.round((val * 100 / total) * 10) / 10) : 0; // Round to one decimal place
        workPv.push([key, p]); //key, percentage
      }

      //X tick
      workXtick = [
        ["0","100","200","300","400","500","600","700","800","900","1000"],
        ["0","50","100","150","200","250","300","350","400","450","500","550","600","650","700","750","800"],
        ["0","50","100","150","200","250","300","350","400","450","500","550","600"],
        ["0","50","100","150","200","250","300","350","400"],
        ["0","25","50","75","100","125","150","175","200"]
      ];

      //Y tick
      let digit = Math.pow(10, String(cmax).length - 2);
      let tmp = Math.ceil(cmax / digit);

      // Y : 6 period
      while (tmp % 6 != 0) {
        tmp++;
      }
      cmax = (tmp * digit);
      for (let j = 0; j < 7; j++) {
        let t = (cmax / 6) * j;
        workYtick.push(String(t));
      }

      sizeDistribution = {
        c_v: workCv,
        p_v: workPv,
        xtick: workXtick,
        ytick: workYtick,
      };
    }

    return {
      result: {
        moleculeListData: moleculeTable,
        moleculeSizeDistribution: sizeDistribution,
        intervalCountDistribution: fragmentsPerMolecule,
      },
      errorMessage: []
    };
	}

	/**
	 * Stats Coverage json
   * (statsdata.inc getStatsCoverage)
	 */
  private makeStatsCoveragetData(jsonData: AlignedAssemblyStatsV4, mappedRefNameTable: RefsNameMapping ) {
    if (!jsonData) {
      return {
        result: null,
        errorMessage: ['makeStatsCoveragetData failed. empty jsonData']
      };
    }

    if (jsonData.version) {
      const versionString = String(jsonData.version);
      const version = parseInt(versionString.replace(/[^0-9]/g, ''), 10);
      if (version >= 4) {
        let assembleGenome = this.getStatsGenome(jsonData as AlignedAssemblyStatsV4);
        let assembleChromosome = this.getStatsChromosome(jsonData as AlignedAssemblyStatsV4, mappedRefNameTable);
        return {
          result: {
            assembleGenome: assembleGenome,
            assembleChromosome: assembleChromosome,
          },
          errorMessage: [],
        };
      } else {
        return {
          result: null,
          errorMessage: ['makeStatsCoveragetData error. (ver ' + version + ")"]
        };
      }
    } else {
      return {
        result: null,
        errorMessage: ['makeStatsCoveragetData error. version is undefind']
      };
    }
  }

	/**
	 * Stats genome
   * (statsdata.inc get_stats_genome)
	 */
	private getStatsGenome(jsonData: AlignedAssemblyStatsV4){
    // items
    let statsGenome: GenomeListData = [];
    if (jsonData?.genome) {
      let i = jsonData.genome;

      statsGenome.push({"key": "Number of contigs", "value": this.valtoComma(i?.number_of_contigs)});
      statsGenome.push({"key": "Min length (Mb)", "value": this.valtoComma1000(i?.min_length)});
      statsGenome.push({"key": "Median length (Mb)", "value": this.valtoComma1000(i?.median_length)});
      statsGenome.push({"key": "Covered sites ratio", "value": this.valtoDecimal3(i?.covered_sites_ratio, true)});
      statsGenome.push({"key": "Average length (Mb)", "value": this.valtoComma1000(i?.average_length)});
      statsGenome.push({"key": "N50 length (Mb)", "value": this.valtoComma1000(i?.N50_length)});
      statsGenome.push({"key": "NG50 length (Mb)", "value": this.valtoComma1000(i?.NG50_length)});
      statsGenome.push({"key": "T50 length (Mb)", "value": this.valtoComma1000(i?.T50_length)});
      statsGenome.push({"key": "Max length (Mb)", "value": this.valtoComma1000(i?.max_length)});
      statsGenome.push({"key": "Total reference length (Mb)", "value": this.valtoComma1000(i?.total_length)});
      statsGenome.push({"key": "Covered length ratio", "value": this.valtoDecimal3(i?.covered_length_ratio, true)});
    }

    return statsGenome;
	}

	/**
	 * Stats Chromosome
   * (statsdata.inc get_stats_chromosome)
	 */
	private getStatsChromosome(jsonData: AlignedAssemblyStatsV4, mappedRefNameTable: RefsNameMapping) : AssembleChromosome | undefined {
    // items
    let chromosomeList: AssembleChromosome = [];

    let tmp: { [key: string]: ChromosomeItem } = {};
    if (jsonData?.chromosome) {
      for (const [k, v] of Object.entries(jsonData.chromosome ?? {})) {
        if (v?.number_of_contigs !=0) {
          const chr = this.parseChr(k) ?? k;
          tmp[chr] = v;
        }
      }
      tmp = this.ksortNatural(tmp);

      for (const [k, v] of Object.entries(tmp)) {
        let chromosome: AssembleChromosomeItem = {};
        chromosome[StatsSvChromosomeColumns.CHR] = { value: mappedRefNameTable?.[k] ?? String(k), chr: k} as StatsTableAnchorCell;
        chromosome[StatsSvChromosomeColumns.COL1] = this.valtoDecimal3(v?.allele_frequency);
        chromosome[StatsSvChromosomeColumns.COL2] = this.valtoComma1000(v?.average_length);
        chromosome[StatsSvChromosomeColumns.COL3] = this.valFromComma1000(v?.covered_length);
        chromosome[StatsSvChromosomeColumns.COL4] = this.valtoDecimal3(v?.covered_length_ratio, true);
        chromosome[StatsSvChromosomeColumns.COL5] = this.valtoDecimal3(v?.covered_sites_ratio, true);
        chromosome[StatsSvChromosomeColumns.COL6] = this.valtoDecimal3(v?.cuts_coverage_average);
        chromosome[StatsSvChromosomeColumns.COL7] = this.valtoDecimal3(v?.cuts_depth_average);
        chromosome[StatsSvChromosomeColumns.COL8] = this.valtoDecimal3(v?.ktuple_coverage_average);
        chromosome[StatsSvChromosomeColumns.COL9] = this.valtoComma1000(v?.max_length);
        chromosome[StatsSvChromosomeColumns.COL10] = this.valtoComma1000(v?.median_length);
        chromosome[StatsSvChromosomeColumns.COL11] = this.valtoComma1000(v?.min_length);
        chromosome[StatsSvChromosomeColumns.COL12] = String(v?.number_of_contigs ?? '');
        chromosome[StatsSvChromosomeColumns.COL13] = this.valFromComma1000(v?.reference_length);
        chromosome[StatsSvChromosomeColumns.COL14] = this.valtoComma1000(v?.total_length);

        chromosomeList.push(chromosome);
      }
    }

    return chromosomeList;
	}

	/**
	 * get sample SV data
   * (stats.php getStatsSampleSVData)
	 */
  private getStatsSampleSVData(svJson: SvEnhancedHsv, mappedRefNameTable: RefsNameMapping ) : StatsSVData {
    let smartydata: StatsSVData = {
      errorMessage: []
    };

    if (!svJson || !mappedRefNameTable) {
      smartydata.errorMessage = ['makeStatsCoveragetData failed. empty jsonData'];
      return smartydata;
    }

    let svchrdata1: SvChrArray = this.initChrArray(mappedRefNameTable);  // key:noOfChr, val:Refname
    
    let {svfRec, svversion, errorMessage} = this.getSvDatalist(svJson, mappedRefNameTable, true);
    if (svfRec.length === 0) {
        // unuse TSV rep
        smartydata.errorMessage = [...errorMessage , "Not found SV data."];
        return smartydata;
    }

    // insertions / deletions, Inversions graph data
    if (svversion && svversion == '2') {
      smartydata.svchart = this.mkSVChartData(svfRec, mappedRefNameTable);
    } else {
      smartydata.svchart = this.mkSVChartData1(svfRec, mappedRefNameTable);
    }
    
    // Summary by SV type
    this.mkPieChartData(svfRec, svchrdata1);

    let sv_chromosome: SvChromosome = [];
    for (const [chr, v] of Object.entries(svchrdata1)) {
      let key: string;
      let link_chr: string | StatsTableAnchorCell;
      if (Number(chr) === 0) {
        key = 'ALL';
        link_chr = this.makeStatsTableLinkCell('ALL', 'ALL');
      } else {
        key = chr;
        link_chr = this.makeStatsTableLinkCell(mappedRefNameTable[chr], key);
      }

      const link_ins = this.makeStatsTableLinkCell(v[StatsSvparam.TYPE_SV_INSERTION + 's'] ?? '0', key, StatsSvparam.TYPE_SV_INSERTION);
      const link_del = this.makeStatsTableLinkCell(v[StatsSvparam.TYPE_SV_DELETION + 's'] ?? '0', key, StatsSvparam.TYPE_SV_DELETION);
      const link_inv = this.makeStatsTableLinkCell(v[StatsSvparam.TYPE_SV_INVERSION + 's'] ?? '0', key, StatsSvparam.TYPE_SV_INVERSION);
      const link_dup = this.makeStatsTableLinkCell(v[StatsSvparam.TYPE_SV_DUPLICATION + 's'] ?? '0', key, StatsSvparam.TYPE_SV_DUPLICATION);
      const link_trans = this.makeStatsTableLinkCell(v[StatsSvparam.TYPE_SV_TRANSLOCATION + 's'] ?? '0', key, StatsSvparam.TYPE_SV_TRANSLOCATION);
      const row_total = v[StatsSvparam.TYPE_SV_VALUE_TOTAL] ?? '0';

      sv_chromosome[Number(chr)] = {
        chr: link_chr,
        ins: link_ins,
        del: link_del,
        inv: link_inv,
        dup: link_dup,
        trans: link_trans,
        total: row_total
      } as SvChromosomeItem;
    }

    // After sorting, convert to a keyless array
    const sortedObj = Object.entries(sv_chromosome)
      .sort(([, a], [, b]) => Number(b) - Number(a))
      .map(([, v]) => v);

    smartydata.svchart_color_del = StatsSvparam.COL_SV_DELETION;
    smartydata.svchart_color_ins = StatsSvparam.COL_SV_INSERTION;
    smartydata.svchart_color_inv = StatsSvparam.COL_SV_INVERSION;
    smartydata.sv_chromosome = sortedObj;
    
    // Make data 
    let svchrdata2: SvChrArray = this.initChrArray(mappedRefNameTable);  // key:noOfChr, val:Refname
    this.mkPieChartData(svfRec, svchrdata2);
    // pie chart data (Order desc)
    smartydata.chrOpts = this.sortPieChartData(svchrdata2, mappedRefNameTable);
    
    let pieChartData: SvPieChartData = {};
    for (const [chr, lists] of Object.entries(svchrdata2)) {
      pieChartData[chr] = {
        values: [],
        total: 0,
        seriesColors: [],
      } as SvPieChartDataItem;

      for (const [type, val] of Object.entries(lists)) {
        if (type == StatsSvparam.PIE_CHART_VALUE_TOTAL){
          pieChartData[chr].total = val;
        }
        else if (type == StatsSvparam.PIE_CHART_COLORS) {
          pieChartData[chr].seriesColors = val;
        }
        else {
          pieChartData[chr].values.push([type + "(" + val + ")", val])

        }
      }
    }

    smartydata.pieChartData = pieChartData;
    smartydata.mappedRefNameTable = mappedRefNameTable;
    smartydata.success = true;
    return smartydata;
  }

  private makeStatsTableLinkCell(value: any, chr?: string, svType?: string) : StatsTableAnchorCell {
    return {
      value: value,
      chr: value != '0' ? chr : undefined,
      svType: value != '0' ? svType : undefined,
    } as StatsTableAnchorCell;
  }

	/**
	 * SV JSON report
   * (svdata.inc get_SV_datalist)
	 */
  public getSvDatalist(svJson: SvEnhancedHsv, mappingTable: any, initfilter: boolean) : {svfRec: SvfRec, svversion: string | null, errorMessage: string[]} {
		let {svfRec, svversion, errorMessage} = this.getSVJSONList(svJson, mappingTable, initfilter);
    svfRec = svfRec ?? [];

    return {svfRec, svversion, errorMessage};
  }

	/**
	 * SV JSON report
   * (svdata.inc get_SVJSON_list)
	 */
  private getSVJSONList(svJson: SvEnhancedHsv, mappingTable: RefsNameMapping, initfilter: boolean) : {svfRec: SvfRec | null, svversion: string | null, errorMessage: string[]} {
    let svfRec: SvfRec = [];
    let svversion = null;
		let errorMessage: string[] = [];
    let jsondata = this.parseSvJsonReport(svJson, mappingTable, initfilter);
    if (jsondata === null){
      errorMessage.push('getSVJSONList failed. empty jsonData');
      return {svfRec: null, svversion, errorMessage};
    }

    svfRec = jsondata.svfRec;
    svfRec.sort((a: any, b: any) => a.start - b.start);
    svversion = '2';
    errorMessage = jsondata.errorMessage;

    return {svfRec, svversion, errorMessage};
  }

	/**
	 * SV JSON parse (main)
   * (hce_parser.inc V_JSON_report_parse)
	 * @param String $filename
	 * @param Array $refs_name_mapping
	 * @param Bool $initfilter
	 */
	private parseSvJsonReport(readdata: SvEnhancedHsv, refs_name_mapping: any, initfilter: boolean = false) : SvJsonParse | null{
    let errorMessage: string[] = [];
    let svfRec: SvfRec = [];

    const versionString = String(readdata?.version ?? '');
    let version: string | number = parseInt(versionString.replace(/[^0-9]/g, ''), 10);

    if (readdata?.version != null && readdata?.svs != null) {
      version = parseInt(String(readdata.version).replace(/[^0-9]/g, ''), 10);
      ({svfRec, errorMessage} = this.parseSvData(readdata.svs, refs_name_mapping, initfilter));
    } else {
      version = 'N/A';
      errorMessage.push('parseSvJsonReport failed version N/A');
    }

    const ret: SvJsonParse = {
      svfRec: svfRec,
      errorMessage: errorMessage,
      version: version,
    }
    return ret;
	}

	/**
	 * SV JSON parse (sub)
   * (hce_parser.inc _svdata_parse)
	 * @version 13.0
	 * @param Array $svdata
	 * @param Array $refs_name_mapping
	 * @param Array $ret
	 * @param Array $errorMessage
	 * @param Bool $initfilter
	 */
	private parseSvData(svdata: SvEnhancedHsvSvsRecord, refs_name_mapping: any, initfilter: boolean) : {svfRec: SvfRec, errorMessage: string[]} {
    let svfRec: SvfRec = [];
    let errorMessage: string[] = [];

    for (const [id, sv] of Object.entries(svdata)) {
      let fRecItem: SvfRecItem = {
        noOfChr: '',
        length: 0,
        SVRefSize: 0,
        SVConSize: 0,
      };
      let check: SvfRecCheck = {};
      
      const SVID = id;
      const refName = sv?.chr_id ?? '-';

      const quality_score = sv?.confidence ?? sv?.quality_score ?? ''; // From v22 onward, the field name changes from `confidence` to `quality_score`
      const reason = sv?.reason ?? ''; // Reason
      const WGM = String(sv?.contig_id ?? ''); // ContigName
      check.diff_size = sv?.length ?? '';
      const type = sv?.type ?? '';
      const zygosity = sv?.zygosity ?? '';

      // Embedded
      const Flags = sv?.Embedded === 'Y' ? 'Embedded_Y' : '0';
      const Embedded = sv?.Embedded === 'Y' ? 'Y' : '';
      if (initfilter && Flags==="Embedded_Y") {  // init, default filter
        continue;
      }

      // region/chr
      const region_chr = sv.region.chr;
      check.refStartCutIndex = region_chr.start_index;
      check.refEndCutIndex = region_chr.end_index;
      check.refStartCutPosition = region_chr.start.min;
      check.refEndCutPosition = region_chr.end.max;
      check.chr_max_start = region_chr.start.max;
      check.chr_min_end = region_chr.end.min;
      check.SVRefSize = region_chr.length;

      // region/contig
      const region_contig = sv.region.contig;
      check.targetFromCutIndex = region_contig.start_index;
      check.targetToCutIndex = region_contig.end_index;
      check.ConStartCutPosition = region_contig.start.min;
      check.ConEndCutPosition = region_contig.end.max;
      check.con_max_start = region_contig.start.max;
      check.con_min_end = region_contig.end.min;
      check.SVConSize = region_contig.length;
      check.id = id;

      if (!this.validationSvData(check, fRecItem, errorMessage)) {
        continue;
      }

      // gene name
      let affected_genes: string[] = [];
      let flat_gene_name_list: string[] = [];

      if (region_chr?.affected_genes != null && Array.isArray(region_chr.affected_genes)) {
        affected_genes = region_chr.affected_genes;
        const cnt_genes = region_chr.affected_genes.length;
        for (let idx = 0; idx < cnt_genes; idx++) {
          flat_gene_name_list.push(region_chr.affected_genes[idx]);
          flat_gene_name_list.push(region_chr?.affected_genes_hrefs?.[idx] ?? '');
        }
      }

      const orientation = 'N';
      let QC_statement = '';  // Note
      let refStartAlignmentUUID = '';  // RefStartAlignmentUUID
      let refEndAlignmentUUID = '';  // RefEndAlignmentUUID

      if (type === StatsSvparam.TYPE_SV_TRANSLOCATION) {// Svparam::TYPE_SV_TRANSLOCATION
        const refName2 = sv?.chr_to_id ?? '-';

        // segment_to
        const segment_to = sv.segment_to;
        let check2: SvfRecCheck2 = {};
        check2.refStartCutIndex2 = segment_to?.start_index ?? 0;
        check2.refEndCutIndex2 = segment_to?.end_index ?? 0;
        check2.refStartCutPosition2 = segment_to?.start?.min ?? 0;
        check2.refEndCutPosition2 = segment_to?.end?.max ?? 0;
        check2.targetFromCutIndex2 = 0;  //ConStartCutIndex2
        check2.targetToCutIndex2 = 0;  //ConEndCutIndex2
        check2.ConStartCutPosition2 = 0;
        check2.ConEndCutPosition2 = 0;
        check2.length2 = segment_to?.length ?? 0;

        // gene name
        let affected_genes2: string[] =[];
        let flat_gene2_name_list: string[] = [];
        if (segment_to?.affected_genes != null && Array.isArray(segment_to.affected_genes)) {
          affected_genes2 = segment_to.affected_genes;
          const cnt_genes2 = segment_to.affected_genes.length;
          for (let idx = 0; idx < cnt_genes2; idx++) {
            flat_gene2_name_list.push(segment_to.affected_genes[idx]);
            flat_gene2_name_list.push(segment_to?.affected_genes_hrefs?.[idx] ?? '')
          }
        }

        if ( refName2 !== '-') {
          check2.id = id;
          if (!this.validationSvtrans(check2, fRecItem, errorMessage)) {
            continue;
          }

          fRecItem.refName2 = refName2;
          let data1_num = this.parseChr(refName2);
          if (data1_num === null) {
            data1_num = refName2;
            errorMessage.push(StatsMessage.WARN_HEADER + "($id) refName2 is not parsed. [refName2]");
          }

          fRecItem.noOfChr2 = data1_num;
          const chr_key = Number(fRecItem.noOfChr2); // nullは0
          fRecItem.mappedRefName2 = refs_name_mapping?.[chr_key] ?? '-';

          fRecItem.geneName2 = flat_gene2_name_list;
          fRecItem.geneNameOrg2 = affected_genes2;
        } else {
          fRecItem.refName2 = refName2;
          fRecItem.noOfChr2 = '-';
          fRecItem.mappedRefName2='-';
        }
      }

      // Chr# 
      let data0_num = this.parseChr(refName);
      if (data0_num === null) {
        data0_num = refName;
        errorMessage.push(StatsMessage.WARN_HEADER + "($id) refName is not parsed. [$refName]");
      }

      fRecItem.noOfChr = data0_num;
      const chr_key = Number(fRecItem.noOfChr); // nullは0
      fRecItem.mappedRefName = refs_name_mapping?.[chr_key] ?? '-';

      fRecItem.chromosome = refName;  // direct
      fRecItem.alignmentFileName = refName;
      fRecItem.SVName = WGM;	// ContigName
      fRecItem.SVID = String(SVID);  // String 
      fRecItem.QClevel = QC_statement;
      fRecItem.embeddedFlg = Embedded;
      fRecItem.type = type;
      fRecItem.Zygosity = zygosity;
      fRecItem.quality_score = quality_score;
      fRecItem.reason = reason;
      fRecItem.flags = Flags;
      fRecItem.orientation = orientation;
      fRecItem.RefStartAlignmentUUID = refStartAlignmentUUID;
      fRecItem.RefEndAlignmentUUID = refEndAlignmentUUID;
      fRecItem.geneName = flat_gene_name_list;
      fRecItem.geneNameOrg = affected_genes;

      svfRec.push(fRecItem);
    }

    return {svfRec, errorMessage};
	}

	/**
	 * Validation SV data
   * (hce_parser.inc _svdata_validation)
	 * @param Array data
	 * @param Array reffRec (mutated)
	 * @param Array refErrorMsg (mutated)
   */
	private validationSvData(data: SvfRecCheck, reffRecItem: SvfRecItem, refErrorMsg: string[]) {
		if (this.isNumeric(data.refStartCutIndex)) {
			reffRecItem.flagStart = Number(data.refStartCutIndex);
		} else {
			refErrorMsg.push(StatsMessage.ERROR_HEADER + "(" + data.id + ") not number. " + data.refStartCutIndex);
			return false;
		}

    if (this.isNumeric(data.refEndCutIndex)) {
			reffRecItem.flagEnd = Number(data.refEndCutIndex);
		} else {
			refErrorMsg.push(StatsMessage.ERROR_HEADER + "(" + data.id + ") not number. " + data.refEndCutIndex);
			return false;
		}

    if (data.refStartCutPosition != null && this.isNumeric(data.refStartCutPosition)) {
			reffRecItem.start = this.convNum(data.refStartCutPosition);
		} else {
			refErrorMsg.push(StatsMessage.ERROR_HEADER + "(" + data.id + ") not number. " + data.refStartCutPosition);
			return false;
		}

    if (data.refEndCutPosition != null && this.isNumeric(data.refEndCutPosition)) {
			reffRecItem.end = this.convNum(data.refEndCutPosition);
		} else {
			refErrorMsg.push(StatsMessage.ERROR_HEADER + "(" + data.id + ") not number. " + data.refEndCutPosition);
			return false;
		}

    if (data.chr_min_end != null && this.isNumeric(data.chr_min_end)) {
			reffRecItem.chr_min_end = this.convNum(data.chr_min_end);
		} else {
			refErrorMsg.push(StatsMessage.ERROR_HEADER + "(" + data.id + ") not number. " + data.chr_min_end);
			return false;
		}

    if (data.chr_max_start != null && this.isNumeric(data.chr_max_start)) {
			reffRecItem.chr_max_start = this.convNum(data.chr_max_start);
		} else {
			refErrorMsg.push(StatsMessage.ERROR_HEADER + "(" + data.id + ") not number. " + data.chr_max_start);
			return false;
		}

    if (this.isNumeric(data.targetFromCutIndex)) {
			reffRecItem.target_from_idx = Number(data.targetFromCutIndex);
		} else {
			refErrorMsg.push(StatsMessage.ERROR_HEADER + "(" + data.id + ") not number. " + data.targetFromCutIndex);
			return false;
		}

    if (this.isNumeric(data.targetToCutIndex)) {
			reffRecItem.target_to_idx = Number(data.targetToCutIndex);
		} else {
			refErrorMsg.push(StatsMessage.ERROR_HEADER + "(" + data.id + ") not number. " + data.targetToCutIndex);
			return false;
		}

    if (data.ConStartCutPosition != null && this.isNumeric(data.ConStartCutPosition)) {
			reffRecItem.conStart = this.convNum(data.ConStartCutPosition);
		} else {
			refErrorMsg.push(StatsMessage.ERROR_HEADER + "(" + data.id + ") not number. " + data.ConStartCutPosition);
			return false;
		}

    if (data.ConEndCutPosition != null && this.isNumeric(data.ConEndCutPosition)) {
			reffRecItem.conEnd = this.convNum(data.ConEndCutPosition);
		} else {
			refErrorMsg.push(StatsMessage.ERROR_HEADER + "(" + data.id + ") not number. " + data.ConEndCutPosition);
			return false;
		}

    if (data.con_min_end != null && this.isNumeric(data.con_min_end)) {
			reffRecItem.con_min_end = this.convNum(data.con_min_end);
		} else {
			refErrorMsg.push(StatsMessage.ERROR_HEADER + "(" + data.id + ") not number. " + data.con_min_end);
			return false;
		}

    if (data.con_max_start != null && this.isNumeric(data.con_max_start)) {
			reffRecItem.con_max_start = this.convNum(data.con_max_start);
		} else {
			refErrorMsg.push(StatsMessage.ERROR_HEADER + "(" + data.id + ") not number. " + data.con_max_start);
			return false;
		}

    if (data.diff_size != null && this.isNumeric(data.diff_size)) {
			reffRecItem.length = this.convNum(data.diff_size);
		} else {
			refErrorMsg.push(StatsMessage.ERROR_HEADER + "(" + data.id + ") not number. " + data.diff_size);
			return false;
		}

    if (data.SVRefSize != null && this.isNumeric(data.SVRefSize)) {
			reffRecItem.SVRefSize = this.convNum(data.SVRefSize);
		} else {
			refErrorMsg.push(StatsMessage.ERROR_HEADER + "(" + data.id + ") not number. " + data.SVRefSize);
			return false;
		}

    if (data.SVConSize != null && this.isNumeric(data.SVConSize)) {
			reffRecItem.SVConSize = this.convNum(data.SVConSize);
		} else {
			refErrorMsg.push(StatsMessage.ERROR_HEADER + "(" + data.id + ") not number. " + data.SVConSize);
			return false;
		}

    return true;
	}

	/**
	 * validation SV Translocation data
   * (hce_parser.inc _svtrans_validation)
	 * @param Array data
	 * @param Array reffRec (mutated)
	 * @param Array refErrorMsg (mutated)
	 */
	private validationSvtrans(data: SvfRecCheck2, reffRecItem: SvfRecItem, refErrorMsg: string[]) {
		if (this.isNumeric(data.refStartCutIndex2)) {
			reffRecItem.flagStart2 = Number(data.refStartCutIndex2);
		} else {
			refErrorMsg.push(StatsMessage.ERROR_HEADER + "(" + data.id + ") not number. " + data.refStartCutIndex2);
			return false;
		}

    if (this.isNumeric(data.refEndCutIndex2)) {
			reffRecItem.flagEnd2 = Number(data.refEndCutIndex2);
		} else {
			refErrorMsg.push(StatsMessage.ERROR_HEADER + "(" + data.id + ") not number. " + data.refEndCutIndex2);
			return false;
		}

    if (data.refStartCutPosition2 != null && this.isNumeric(data.refStartCutPosition2)) {
			reffRecItem.start2 = this.convNum(data.refStartCutPosition2);
		} else {
			refErrorMsg.push(StatsMessage.ERROR_HEADER + "(" + data.id + ") not number. " + data.refStartCutPosition2);
			return false;
		}

    if (data.refEndCutPosition2 != null && this.isNumeric(data.refEndCutPosition2)) {
			reffRecItem.end2 = this.convNum(data.refEndCutPosition2);
		} else {
			refErrorMsg.push(StatsMessage.ERROR_HEADER + "(" + data.id + ") not number. " + data.refEndCutPosition2);
			return false;
		}

    if (this.isNumeric(data.targetFromCutIndex2)) {
			reffRecItem.target_from_idx2 = Number(data.targetFromCutIndex2);
		} else {
			refErrorMsg.push(StatsMessage.ERROR_HEADER + "(" + data.id + ") not number. " + data.targetFromCutIndex2);
			return false;
		}

    if (this.isNumeric(data.targetToCutIndex2)) {
			reffRecItem.target_to_idx2 = Number(data.targetToCutIndex2);
		} else {
			refErrorMsg.push(StatsMessage.ERROR_HEADER + "(" + data.id + ") not number. " + data.targetToCutIndex2);
			return false;
		}

    if (data.ConStartCutPosition2 != null && this.isNumeric(data.ConStartCutPosition2)) {
			reffRecItem.conStart2 = this.convNum(data.ConStartCutPosition2);
		} else {
			refErrorMsg.push(StatsMessage.ERROR_HEADER + "(" + data.id + ") not number. " + data.ConStartCutPosition2);
			return false;
		}

    if (data.ConEndCutPosition2 != null && this.isNumeric(data.ConEndCutPosition2)) {
			reffRecItem.conEnd2 = this.convNum(data.ConEndCutPosition2);
		} else {
			refErrorMsg.push(StatsMessage.ERROR_HEADER + "(" + data.id + ") not number. " + data.ConEndCutPosition2);
			return false;
		}

    if (data.length2 != null && this.isNumeric(data.length2)) {
			reffRecItem.length2 = this.convNum(data.length2);
		} else {
			refErrorMsg.push(StatsMessage.ERROR_HEADER + "(" + data.id + ") not number. " + data.length2);
			return false;
		}

    return true;
	}

	/**
	 * Stats sv chart
   * (svdata.inc mkSVChartData)
	 */
  private mkSVChartData(fRec: SvfRec, mappingTable: Record<string, any> ) : Svchart {
    let temp: Record<string, Record<string, number>> = {};
    let i: number = 0;

    //foreach($fRec as $key=>$aRec){
    for (const [key, aRec] of Object.entries(fRec)) {
      if (!mappingTable?.[aRec.noOfChr] && mappingTable?.[aRec['noOfChr']] != 0) {
        continue;
      }
		
      let chart;
			const type = aRec.type;
			if (type == StatsSvparam.TYPE_SV_DELETION) {
				chart = 'InDel';
				const l = Math.floor(aRec.SVRefSize / 1000) * -1;
				if       (l < -200) {	i = 0;
				} else if(l < -100) {	i = 1;
				} else if(l < -50) {	i = 2;
				} else if(l < -40) {	i = 3;
				} else if(l < -30) {	i = 4;
				} else if(l < -20) {	i = 5;
				} else if(l < -10) {	i = 6;
				} else if(l <= -0 ) {	i = 7; }
			} else if (type == StatsSvparam.TYPE_SV_INSERTION) {
				chart = 'InDel';
				let l = Math.floor(aRec.SVConSize / 1000);
				if       (l > 200) {i = 15;
				} else if(l > 100) {i = 14;
				} else if(l > 50) {	i = 13;
				} else if(l > 40) {	i = 12;
				} else if(l > 30) {	i = 11;
				} else if(l > 20) {	i = 10;
				} else if(l > 10) {	i = 9;
				} else if(l >= 0) {	i = 8;	}
			} else if (type == StatsSvparam.TYPE_SV_INVERSION) {
				chart = 'Inv';
				let l = Math.floor(aRec.SVRefSize / 1000);
				if       (l > 200) { i = 7;
				} else if(l > 100) {i = 6;
				} else if(l > 50) {	i = 5;
				} else if(l > 40) {	i = 4;
				} else if(l > 30) {	i = 3;
				} else if(l > 20) {	i = 2;
				} else if(l > 10) {	i = 1;
				} else if(l >= 0) {	i = 0;	}
			} else {
				continue;
			}

      temp[chart] ??= {};
      temp[chart][i] ??= 0;
			temp[chart][i] += 1;
		}

    // Deletions / Insertions
    let InDelV: number[] = [];
		for (let i = 0; i < 16; i++) {
      InDelV.push(temp?.['InDel']?.[i] ?? 0);
    }

    // Inversions
    let InvV = [];
		for (let i = 0; i < 8; i++) {
      InvV.push(temp?.['Inv']?.[i] ?? 0);
    }

    const svchart: Svchart = {
      InDel: {
        v: InDelV,
        tick: ['<-200','-200','-100','-50','-40','-30','-20','-10','10','20','30','40','50','100','200','>200'],
      },
      Inv: {
        v: InvV,
        tick: ['10','20','30','40','50','100','200','>200'],
      }
    }

    return svchart;
  }

	/**
	 * Stats sv chart 1
   * (svdata.inc mkSVChartData1)
	 */
  private mkSVChartData1(fRec: SvfRec, mappingTable:  Record<string, any> ) {
    let temp: Record<string, Record<number, number>> = {};

    for (const [key, aRec] of Object.entries(fRec)) {
      if (!mappingTable?.[aRec.noOfChr] && mappingTable?.[aRec.noOfChr] != 0) {
        continue;
      }
		
      let chart;
      let i: number = 0;

      const type = aRec.type;
			let l = Math.floor(aRec.length / 1000);
			if (type == StatsSvparam.TYPE_SV_DELETION) {
				chart = 'InDel';
				l *= -1;
				if       (l < -200) {	i = 0;
				} else if(l < -100) {	i = 1;
				} else if(l < -50) {	i = 2;
				} else if(l < -40) {	i = 3;
				} else if(l < -30) {	i = 4;
				} else if(l < -20) {	i = 5;
				} else if(l < -10) {	i = 6;
				} else if(l <= -0) {  i = 7; }
			} else if (type == StatsSvparam.TYPE_SV_INSERTION) {
				chart = 'InDel';
				if       (l > 200) {i = 15;
				} else if(l > 100) {i = 14;
				} else if(l > 50) {	i = 13;
				} else if(l > 40) {	i = 12;
				} else if(l > 30) {	i = 11;
				} else if(l > 20) {	i = 10;
				} else if(l > 10) {	i = 9;
				} else if(l >= 0) {	i = 8; }
			} else if (type == StatsSvparam.TYPE_SV_INVERSION) {
				chart = 'Inv';
				if       (l > 200) {i = 7;
				} else if(l > 100) {i = 6;
				} else if(l > 50) {	i = 5;
				} else if(l > 40) {	i = 4;
				} else if(l > 30) {	i = 3;
				} else if(l > 20) {	i = 2;
				} else if(l > 10) {	i = 1;
				} else if(l >= 0) {	i = 0; }
			} else {
				continue;
			}

      if (!(temp?.[chart]?.[i] != null)) {
        temp[chart][i] = 0;
      }
			temp[chart][i]++;
		}

    // Deletions / Insertions
    let InDelV = [];
		for (let i = 0; i < 16; i++) {
      InDelV.push(temp?.['InDel']?.[i] ?? 0);
    }

    // Inversions
    let InvV = [];
		for (let i = 0; i < 8; i++) {
      InvV.push(temp?.['Inv']?.[i] ?? 0);
    }

    const svchart: Svchart = {
      InDel: {
        v: InDelV,
        tick: ['<-200','-200','-100','-50','-40','-30','-20','-10','10','20','30','40','50','100','200','>200'],
      },
      Inv: {
        v: InvV,
        tick: ['10','20','30','40','50','100','200','>200'],
      }
    }

    return svchart;
  }

  /* Sort as natural numbers */
  private ksortNatural<T extends Record<string, any>>(obj: T): T {
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    const sortedKeys = Object.keys(obj).sort((a, b) => collator.compare(a, b));
    const out = {} as T;
    for (const k of sortedKeys) {
      out[k as keyof T] = obj[k];
    }
    return out;
  }

	/**
	 * Create an empty array with the same keys as the Mapping Table
   * (stats.php _initChrArray)
   */
	private initChrArray(chrNmList: RefsNameMapping): SvChrArray {
    const ret: Record<string, Record<string, number>> = {};
    for (const chr of Object.keys(chrNmList)) {
      ret[chr] = {};
    }

    return ret;
  }

  /**
	 * Utils functions
   * Downscale by the given factor; format with comma-separated integer part and 3 decimal places
	 */
	private valtoComma(val: any, ratio: number = 1) {
    if (val === undefined) {
      return '';
    } else if (this.isNumeric(val)) {
      // Format as "1,234.000" (comma-separated thousands, fixed 3 decimals)
      const withThree = new Intl.NumberFormat('en-US', {
        useGrouping: true,
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      }).format(val / ratio);

      // Trim trailing zeros and the decimal point（"1,234.000" > "1,234" / "1,234.120" > "1,234.12"）
      return withThree.replace(/\.?0+$/, '');
		} else {
			return' - ';
		}
	}

  /* Scale to 1/1000; format with comma-separated integer part and 3 decimals */
	private valtoComma1000(val: any) {
    return this.valtoComma(val, 1000);
	}

  /* Scale to 1/1000000; format with comma-separated integer part and 3 decimals */
	private valtoComma1000000(val: any) {
    return this.valtoComma(val, 1000000);
	}

  /* Scale to 1/1000; format with comma-separated integer part and 3 decimals */
	private valFromComma1000(val: any) {
    return this.valtoComma(val, 0.001);
	}

  /* 
   * Format as a string with 3 decimal places
   * isPercent：multiply by 100 and append "%"
   */
	private valtoDecimal3(val: any, isPercent: boolean = false) {
    if (val === undefined) {
        return '';
    } else if (this.isNumeric(val)) {
      // Format as "1,234.000" (comma-separated thousands, fixed 3 decimals)
      const withThree = new Intl.NumberFormat('en-US', {
        useGrouping: true,
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      }).format(val * (isPercent ? 100 : 1));

      // Trim trailing zeros and the decimal point（"1,234.000" > "1,234" / "1,234.120" > "1,234.12"）
      return withThree.replace(/\.?0+$/, '') + (isPercent ? '%' : '');
		} else {
			return' - ';
		}
	}

  /* 
   * Check if the value can be converted to a number
   */
	private isNumeric(val: any) {
    return val != null && !isNaN(Number(val))
  }

  /**
	 * convert string to number(Int)
	 * Scale to thousandths (×1,000) and cast to int
	 */
	public convNum(str: string | number): number {
    const parts = String(str).split(".");
    const intPart = parts[0] ?? "0";
    const fracPart = (parts[1] ?? "0").substring(0, 3).padEnd(3, "0");
    const joined = `${intPart}${fracPart}`;

    return Number.parseInt(joined, 10);
	}

	/**
	 * chromosome parse
   * (hce_parser.inc chr_parse)
	 */
	private parseChr(val: string | number) : string | number | null {
    const str = String(val ?? '');
    let ret: number | string | null = null;

    // chr (separator _)
    if (ret === null) {
      const matches = str.match("/^chr(\d+?|x|y)_/i");
      if (matches) {
        const token = matches[1].toLowerCase();
        if (token === "x") {
          ret = 23;
        } else if (token === "y") {
          ret = 24;
        } else {
          ret = Math.abs(parseInt(token, 10));
        }
      }
    }

    // (Commented out in the existing HCE)
    // // 1. NCBI format
    // if ($ret === null) {
    // 	$reg = "/(.*)(?:\.([^.]+$))/";
    // 	preg_match($reg, $str, $retArr);
    // 	if (isset($retArr[1])) {
    // 		$ret = abs(substr($retArr[1], -2));
    // 	}
    // }
    
    // 2. _Chr(\d+) or _chr(\d+)
    // 3. _Chr(\w) 
    if (ret === null) {
      const matches = str.match("/chr(\d+|x|y)/i");
      if (matches) {
        const token = matches[1].toLowerCase();
        if (token === "x") {
          ret = 23;
        } else if (token === "y") {
          ret = 24;
        } else if (this.isNumeric(token)) {
          ret = Math.abs(parseInt(token, 10));
        }
      }
    }

    // 4. _ALT_REF_LOCI_(\d+) 
    if (ret === null) {
      const matches = str.match("/_ALT_REF_LOCI_(\w+)/");
      if (matches) {
        ret = Math.abs(parseInt(matches[1], 10));
      }
    }

    // 5. X or Y 
    if (ret === null) {
      if(str.toLowerCase() == "x") {
        ret = 23;
      } else if (str.toLowerCase() == "y") {
        ret = 24;
      }
    }

    // 6. chr (\d+) 
    if (!ret) { // Include 0 as a target value, same as PHP's empty()
      const matches = str.match("/chr\s+(\d+?|x|y)_/i");
      if (matches) {
        const token = matches[1].toLowerCase();
        if (token === "x") {
          ret = 23;
        } else if (token === "y") {
          ret = 24;
        } else {
          ret = Math.abs(parseInt(token, 10));
        }
      }
    }

    // 7. chr_(\d+)
    if (!ret) { // Include 0 as a target value, same as PHP's empty()
      const matches = str.match("/chr_(\d+?|x|y)_/i");
      if (matches) {
        const token = matches[1].toLowerCase();
        if (token === "x") {
          ret = 23;
        } else if (token === "y") {
          ret = 24;
        } else  {
          ret = Math.abs(parseInt(token, 10));
        }
      }
    }

    // 8. _chromosome (\d+) 
    if (!ret) { // Include 0 as a target value, same as PHP's empty()
      const matches = str.match("/chromosome\s+(\d+|\w+)/i");
      if (matches) {
        const token = matches[1].toLowerCase();
        if (token === "x") {
          ret = 23;
        } else if (token === "y") {
          ret = 24;
        } else if (this.isNumeric(token)) {
          ret = Math.abs(parseInt(token, 10));
        }
      }
    }

    // no match
    if(ret ===null){
      ret = str;
    }

    return ret;
	}

	/**
	 * Format SV data for a pie chart
   * (stats.php mkPieChartData)
	 */
	private mkPieChartData(fRec: SvfRec, refRet: Record<string, Record<string, number>>, graph = true){
		refRet[0] = {};
		refRet[0][StatsSvparam.PIE_CHART_VALUE_TOTAL] = 0;

		if (!graph) {  // for List
			refRet[StatsSvparam.PIE_CHART_CHOICE_DEFAULT]['tlA'] = 0;
			refRet[StatsSvparam.PIE_CHART_CHOICE_DEFAULT]['tlB'] = 0;
		}

    for (const [key, aRec] of Object.entries(fRec)) {
      const chr = aRec.noOfChr;
			const type = aRec.type + 's';

      // Initial value
      refRet[chr] ??= {}; // If `chr` is not present, initialize the array
      refRet[chr][type] ??= 0; // Add `type: 0` if the type field does not exist
      refRet[chr][StatsSvparam.PIE_CHART_VALUE_TOTAL] ??= 0; // Add `PIE_CHART_VALUE_TOTAL: 0` if the type field does not exist
      refRet[0][type] ??= 0; // If `type` is missing in ALL(0), add it with value 0

      // increment
      refRet[chr][type] += 1;
			refRet[chr][StatsSvparam.PIE_CHART_VALUE_TOTAL] += 1;
      refRet[0][type] += 1;
			refRet[0][StatsSvparam.PIE_CHART_VALUE_TOTAL] += 1;
		}

    for (const k of Object.keys(refRet)) {
      refRet[k] = Object.fromEntries(
        Object.entries(refRet[k]).sort(([, a], [, b]) => (b as number) - (a as number))
      );
    }
	}

	/**
	 * Sort pie chart data in descending order by value and add the color list for each item
   * (stats.php sortPieChartData)
	 */
	private sortPieChartData(ret: Record<string, Record<string, any>>, mappedRefNameTable: RefsNameMapping) : SvChrOpts {
		let chrOpts: SvChrOpts= {};

    for (const [aChr, aChrList] of Object.entries(ret)) {
			if (Object.keys(aChrList).length < 1){
        delete ret[aChr];
				continue;
			}

      let label;
			if (Number(aChr) === 0) {
				label = 'ALL';
			} else {
				label = mappedRefNameTable[aChr];
			}

      chrOpts[aChr] = label;
			ret[aChr][StatsSvparam.PIE_CHART_COLORS] = [];

      for (const [type, val] of Object.entries(ret[aChr])) {
				let typeColor: string | undefined;
				switch(type){
					case StatsSvparam.TYPE_SV_DELETION + 's':
						typeColor = StatsSvparam.COL_SV_DELETION;
						break;
					case StatsSvparam.TYPE_SV_INSERTION + 's':
						typeColor = StatsSvparam.COL_SV_INSERTION;
						break;
					case StatsSvparam.TYPE_SV_INVERSION + 's':
						typeColor = StatsSvparam.COL_SV_INVERSION;
						break;
					case StatsSvparam.TYPE_SV_TRANSLOCATION + 's':
						typeColor = StatsSvparam.COL_SV_TRANSLOCATION;
						break;
					case StatsSvparam.TYPE_SV_DUPLICATION + 's':
						typeColor = StatsSvparam.COL_SV_DUPLICATION;
						break;
					default:
						if (type != StatsSvparam.PIE_CHART_VALUE_TOTAL && type != StatsSvparam.PIE_CHART_COLORS) {
							typeColor = StatsSvparam.COL_SV_OTHER;
						}
				}
				if(typeColor){
					ret[aChr][StatsSvparam.PIE_CHART_COLORS].push(typeColor);
				}
			}
		}

    return chrOpts;
  }

  buildStatsSvFromRaw(
    svJson: SvEnhancedHsv,
    refNameMap: RefsNameMapping
  ): StatsSVData {
    return this.getStatsSampleSVData(svJson, refNameMap);
  }

  private buildRegion(src: any): SvEnhancedHsvSvsRegion {
    return {
      start_index: src.start.index,
      end_index: src.end.index,

      start: {
        min: src.start.min,
        max: src.start.max,
        index: src.start.index,
        pos_ind: src.start.pos_ind,
        pos_kb: src.start.pos_kb,
      },

      end: {
        min: src.end.min,
        max: src.end.max,
        index: src.end.index,
        pos_ind: src.end.pos_ind,
        pos_kb: src.end.pos_kb,
      },

      length: src.length,

      is_clear: src.is_clear ?? true,
      is_degenerated: src.is_degenerated ?? false,
      is_fuzzy: src.is_fuzzy ?? false,
      is_pure_overlapped: src.is_pure_overlapped ?? false,

      affected_genes: src.affected_genes ?? [],
      affected_genes_hrefs: src.affected_genes_hrefs ?? [],
      affected_regions: src.affected_regions ?? [],
    };
  }

  /**
   * Convert SV data to use on this view
   */
  convertFromSvResponse(sv: SvResponse): SvEnhancedHsv {
    const svs: SvEnhancedHsvSvsRecord = {};

    for (const [id, item] of Object.entries(sv.svs)) {
      svs[id as `${number}`] = {
        chr_id: item.chr_id,
        chr_to_id: item.chr_to_id,
        contig_id: item.contig_id,

        type: item.type,
        zygosity: item.zygosity,
        Embedded: item.Embedded,

        length: item.length,
        confidence: item.quality_score,
        quality_score: item.quality_score,
        reason: item.reason,

        region: {
          chr: this.buildRegion(item.region.chr),
          contig: this.buildRegion(item.region.contig),
        },

        segment_to: item.segment_to
          ? this.buildRegion(item.segment_to)
          : undefined,
      };
    }

    return {
      hce_version: sv.hce_version,
      version: sv.version,
      svs,
    };
  }

}
